/**
 * Step 1 — Ad basics: name, status, page, Instagram actor.
 *
 * Page + Instagram actor live HERE (not on the variant creative steps)
 * because all variants need them and Meta requires a Page on every
 * creative. Putting them on each variant step would duplicate the
 * picker + selector logic.
 */
import React from 'react';

import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import type {
  MetaAdCreateStatus,
  MetaPage,
} from '@/services/api/metaAds';

import { STATUS_OPTIONS } from '../../constants/metaAdMatrix';
import { PAGE_FIELD_IDS } from './AdBasicsStep.constants';

export interface AdBasicsStepProps {
  adName: string;
  status: MetaAdCreateStatus;
  pageId: string;
  instagramActorId: string;
  /** Pages picker. The wizard loads via `metaAdsApi.listPages()` and
   *  passes the results in. Empty list = no pages available (probably
   *  a permissions issue) — surface a warning Alert. */
  availablePages: MetaPage[];
  isLoadingPages: boolean;
  /** When true, the Page picker is replaced with a read-only summary line.
   *  Set by the wizard when the parent campaign objective is OUTCOME_LEADS
   *  AND the parent ad set has a `promoted_object.page_id` we can inherit —
   *  Meta requires the ad's page_id to match the ad set's for LEADS. */
  isPageInherited?: boolean;
  /** When true, the Page picker is replaced with a "will inherit from the
   *  ad set" info banner. Set by the wizard when the parent campaign is
   *  OUTCOME_LEADS but the ad set hasn't been picked yet — otherwise
   *  users pick a Page here, then get it silently overwritten on the
   *  next step when they pick the ad set. Suppressing the dropdown up
   *  front removes the wasted selection AND the silent-overwrite
   *  surprise. */
  isLeadsPendingAdSet?: boolean;
  onChangeName: (name: string) => void;
  onChangeStatus: (status: MetaAdCreateStatus) => void;
  onChangePageId: (pageId: string) => void;
  onChangeInstagramActorId: (id: string) => void;
  errors?: {
    name?: string;
    pageId?: string;
    instagramActorId?: string;
  };
}

export const AdBasicsStep: React.FC<AdBasicsStepProps> = ({
  adName,
  status,
  pageId,
  instagramActorId,
  availablePages,
  isLoadingPages,
  isPageInherited = false,
  isLeadsPendingAdSet = false,
  onChangeName,
  onChangeStatus,
  onChangePageId,
  onChangeInstagramActorId,
  errors,
}) => {
  const hasNoPages = !isLoadingPages && availablePages.length === 0;
  const inheritedPageName =
    isPageInherited
      ? availablePages.find((p) => p.id === pageId)?.name ?? pageId
      : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ad basics</h3>
        <p className="text-sm text-gray-600">
          Name your ad and pick the Page it runs from.
        </p>
      </div>

      {hasNoPages && (
        <Alert variant="warning">
          <p className="text-sm font-medium">No Facebook Pages available</p>
          <p className="text-sm mt-1">
            Meta requires a Page on every ad creative, and the linked
            account doesn’t expose any Pages this user can promote. Add
            the user as a Page admin in Meta Business Suite, or relink
            this Meta account from the Settings page.
          </p>
        </Alert>
      )}

      <div>
        <label
          htmlFor="meta-ad-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Ad name <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-ad-name"
          type="text"
          value={adName}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g. Summer Promo — Landing 1"
          maxLength={400}
          aria-invalid={Boolean(errors?.name)}
          aria-describedby={errors?.name ? 'meta-ad-name-error' : undefined}
          required
        />
        {errors?.name && (
          <p id="meta-ad-name-error" className="text-sm text-red-600 mt-1">
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-ad-page"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Facebook Page <span className="text-red-500" aria-label="required">*</span>
        </label>
        {isPageInherited ? (
          // Use a disabled + readOnly input so the label's htmlFor
          // association is honored by screen readers (a `<div>` is not
          // labelable, per Copilot round 1 on PR #216). The inheritance
          // caption stays outside the input and is announced via
          // aria-describedby. When errors.pageId is set (unusual for
          // the inherited branch but possible if a future validator
          // adds one), also announce the error id — see the pending
          // branch below for the same pattern (Copilot round 3 on PR
          // #218).
          <>
            <input
              id="meta-ad-page"
              type="text"
              value={inheritedPageName ?? pageId}
              readOnly
              disabled
              aria-readonly="true"
              aria-invalid={Boolean(errors?.pageId)}
              aria-describedby={
                errors?.pageId
                  ? `${PAGE_FIELD_IDS.inheritedCaption} ${PAGE_FIELD_IDS.error}`
                  : PAGE_FIELD_IDS.inheritedCaption
              }
              className="block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed"
            />
            <p
              id={PAGE_FIELD_IDS.inheritedCaption}
              className="text-xs text-gray-500 mt-1"
            >
              Inherited from the parent ad set. Meta requires the ad and
              its ad set to share a Page for LEADS-objective campaigns.
            </p>
          </>
        ) : isLeadsPendingAdSet ? (
          // LEADS parent, Page not yet resolved from the ad set.
          // Reached in two shapes: the wizard was launched without an
          // `adSetId` prop (step 2 is where the user will pick), OR
          // an `adSetId` prop was passed and the ad-sets query hasn't
          // resolved yet. Suppress the dropdown either way — whatever
          // the user picks here would get silently overwritten by the
          // inheritance effect on step 2 (or on query resolve).
          // Placeholder is deliberately neutral so it's accurate in
          // both shapes (Copilot round 1 on PR #218). Render the
          // readonly input shape so screen readers still see a labeled
          // control associated with the "Facebook Page" label above.
          //
          // aria-invalid + aria-describedby-with-error-id: when the
          // wizard's step-1 validator surfaces a loading / fetch-
          // failure / page-less-ad-set message via errors.pageId,
          // screen readers need to reach the error text from the
          // input. Without these, the error paragraph below is
          // orphaned from the control (Copilot round 3 on PR #218).
          <>
            <input
              id="meta-ad-page"
              type="text"
              value=""
              readOnly
              disabled
              placeholder="Inherits from the parent ad set"
              aria-readonly="true"
              aria-invalid={Boolean(errors?.pageId)}
              aria-describedby={
                errors?.pageId
                  ? `${PAGE_FIELD_IDS.pendingCaption} ${PAGE_FIELD_IDS.error}`
                  : PAGE_FIELD_IDS.pendingCaption
              }
              className="block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 placeholder:text-gray-500 disabled:cursor-not-allowed"
            />
            <p
              id={PAGE_FIELD_IDS.pendingCaption}
              className="text-xs text-gray-500 mt-1"
            >
              Meta requires LEADS ads to share a Page with their parent
              ad set.
            </p>
          </>
        ) : (
          <select
            id="meta-ad-page"
            value={pageId}
            onChange={(e) => onChangePageId(e.target.value)}
            disabled={isLoadingPages || hasNoPages}
            aria-invalid={Boolean(errors?.pageId)}
            aria-describedby={errors?.pageId ? PAGE_FIELD_IDS.error : undefined}
            required
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">
              {isLoadingPages ? 'Loading Pages…' : 'Select a Page'}
            </option>
            {availablePages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        {errors?.pageId && (
          <p id={PAGE_FIELD_IDS.error} className="text-sm text-red-600 mt-1">
            {errors.pageId}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-ad-instagram"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Instagram actor ID{' '}
          <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <Input
          id="meta-ad-instagram"
          type="text"
          value={instagramActorId}
          onChange={(e) => onChangeInstagramActorId(e.target.value)}
          placeholder="Numeric Instagram user ID"
          aria-invalid={Boolean(errors?.instagramActorId)}
          aria-describedby={
            errors?.instagramActorId ? 'meta-ad-instagram-error' : undefined
          }
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank to run on Facebook only. Set the linked Instagram
          account’s numeric ID to also serve on Instagram surfaces.
        </p>
        {errors?.instagramActorId && (
          <p id="meta-ad-instagram-error" className="text-sm text-red-600 mt-1">
            {errors.instagramActorId}
          </p>
        )}
      </div>

      <fieldset className="space-y-3">
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          Initial status
        </legend>
        {STATUS_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-start gap-3">
            <input
              type="radio"
              name="meta-ad-status"
              value={opt.value}
              checked={status === opt.value}
              onChange={() => onChangeStatus(opt.value)}
              className="mt-1"
              aria-describedby={`meta-ad-status-${opt.value}-desc`}
            />
            <div>
              <div className="text-sm font-medium text-gray-900">
                {opt.label}
              </div>
              <div
                id={`meta-ad-status-${opt.value}-desc`}
                className="text-sm text-gray-600"
              >
                {opt.description}
              </div>
            </div>
          </label>
        ))}
      </fieldset>
    </div>
  );
};
